"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { formatCurrency } from "@/app/(general-header)/botb-rewards/utils";
import { CompetitionData } from "@/app/components/ActiveCompetitions/types";
import ToastNotification from "@/app/components/ToastNotification/toastNotification";
import { useGetBotbPassCompetitionsQuery, useGetSavingsSummaryQuery } from "@/app/redux/features/api/membersHome";
import ChevronDownIcon from "@/app/shared-components/icons/ChevronDownIcon";
import CloseModalIcon from "@/app/shared-components/icons/CloseModalIcon";
import { useAppDispatch } from "@/app/redux/hooks";
import { cancelSubscription } from "../../subscription/subscription-service";
import { OnCloseReason } from "../../subscription/interfaces";
import { openInvoicePaymentModal } from "../openInvoicePaymentModal";
import { useManagementSubscriptionContext } from "../Context";

const COLORS = {
  "navigation-orange": {
    title: "text-nav-orange",
    bgBadge: "bg-nav-orange/20",
    textBadge: "text-nav-badge-orange",
  },
  "navigation-blue": {
    title: "text-nav-blue",
    bgBadge: "bg-nav-blue/20",
    textBadge: "text-nav-badge-blue",
  },
  "navigation-purple": {
    title: "text-nav-purple",
    bgBadge: "bg-nav-purple/20",
    textBadge: "text-nav-badge-purple",
  },
  "navigation-turquoise": {
    title: "text-nav-turquoise",
    bgBadge: "bg-nav-turquoise/20",
    textBadge: "text-nav-badge-turquoise",
  },
  "navigation-red": {
    title: "text-nav-red",
    bgBadge: "bg-nav-red/20",
    textBadge: "text-nav-badge-red",
  },
  "navigation-azure": {
    title: "text-nav-azure",
    bgBadge: "bg-nav-azure/20",
    textBadge: "text-nav-badge-azure",
  },
  "navigation-green": {
    title: "text-nav-green",
    bgBadge: "bg-nav-green/20",
    textBadge: "text-nav-badge-green",
  },
  "navigation-dark-fuchsia": {
    title: "text-nav-dark-fuchsia",
    bgBadge: "bg-nav-dark-fuchsia/20",
    textBadge: "text-nav-badge-dark-fuchsia",
  },
  "navigation-dark-blue": {
    title: "text-nav-dark-blue",
    bgBadge: "bg-nav-dark-blue/20",
    textBadge: "text-nav-badge-dark-blue",
  },
} as const;

function CompetitionRow({ competition, isLast }: { competition: CompetitionData; isLast: boolean }) {
  const colors = COLORS[competition.NavigationColour as keyof typeof COLORS];
  const ticketCount = competition.TicketsInComp ?? 0;

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-center justify-between gap-3 w-full">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative rounded shrink-0 size-10 lg:size-12 overflow-hidden bg-gray-11">
            {competition.ImageUrl && (
              <img src={competition.ImageUrl} alt={competition.Title} className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex flex-col gap-1 items-start min-w-0">
            <div
              className={`font-jost text-sm lg:text-base font-semibold leading-5 lg:leading-5.5 truncate w-full ${colors?.title ?? "text-secondary"}`}>
              {competition.Title}
            </div>
            <div
              className={`inline-flex items-center justify-center px-1 py-0.5 rounded ${colors?.bgBadge ?? "bg-gray-6"}`}>
              <span
                className={`font-jost text-[10px] lg:text-[11px] font-medium leading-3 lg:leading-3.5 uppercase whitespace-nowrap ${colors?.textBadge ?? "text-secondary"}`}>
                ENDS {competition.EndDate.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
        <div className="shrink-0 font-jost text-xs lg:text-sm font-semibold leading-4 lg:leading-5 text-secondary whitespace-nowrap">
          {ticketCount} {ticketCount === 1 ? "Ticket" : "Tickets"}
        </div>
      </div>
      {!isLast && <div className="w-full h-px bg-black/8" />}
    </div>
  );
}

export default function PaymentOverdueCancelModal({
  invoiceId,
  invoiceAmountDue,
  onClose,
}: {
  invoiceId: string;
  invoiceAmountDue: number;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const { setIsInvoiceProcessing } = useManagementSubscriptionContext();
  const [isCompetitionsExpanded, setIsCompetitionsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<"featured" | "exclusive">("featured");
  const [isCancelling, setIsCancelling] = useState(false);
  const { data: savings } = useGetSavingsSummaryQuery();
  const { data: competitionsData } = useGetBotbPassCompetitionsQuery();

  const { featured, exclusive, totalCompetitions, totalTickets } = useMemo(() => {
    const all = [...(competitionsData?.played ?? []), ...(competitionsData?.lost ?? [])];
    const featuredList = all.filter(c => c.CompetitionTypePrefix !== "SC");
    const exclusiveList = all.filter(c => c.CompetitionTypePrefix === "SC");
    return {
      featured: featuredList,
      exclusive: exclusiveList,
      totalCompetitions: all.length,
      totalTickets: all.reduce((sum, c) => sum + (c.TicketsInComp ?? 0), 0),
    };
  }, [competitionsData]);

  const activeCompetitions = activeTab === "featured" ? featured : exclusive;
  const amountLabel = formatCurrency(invoiceAmountDue / 100);
  const savingsLabel = formatCurrency(savings?.LifetimeTotal ?? 0);

  useEffect(() => {
    document.querySelector("body")?.classList.add("overflow-hidden");
    return () => {
      document.querySelector("body")?.classList.remove("overflow-hidden");
    };
  }, []);

  const handlePayAndStay = () => {
    onClose();
    openInvoicePaymentModal(dispatch, invoiceId, {
      onClose: (reason?: OnCloseReason) => {
        if (reason === "successSubmit") {
          setIsInvoiceProcessing(true);
        }
      },
    });
  };

  const handleCancelAndVoid = () => {
    if (isCancelling) return;
    setIsCancelling(true);
    cancelSubscription()
      .then(async res => {
        const data = await res.json().catch(() => null);
        if (res.status <= 200 && res.status >= 400) {
          const message = Array.isArray(data?.message)
            ? data.message.join(", ")
            : data?.message || data?.error || "Something went wrong. Please try again later.";
          toast.error(<ToastNotification message={message} />);
          setIsCancelling(false);
          return;
        }

        const params = new URLSearchParams({
          botbPassCancelled: "1",
          tickets: String(totalTickets),
          competitions: String(totalCompetitions),
        });
        location.href = `/?${params.toString()}`;
      })
      .catch(err => {
        console.error(err);
        toast.error(<ToastNotification message="Something went wrong. Please try again later." />);
        setIsCancelling(false);
      });
  };

  return (
    <div>
      <div aria-hidden="true" className="fixed inset-0 z-[9999] bg-black/70 max-lg:hidden" />
      <div className="fixed inset-0 z-[99999] w-full min-h-full overflow-x-hidden overflow-y-auto bg-white lg:bg-transparent">
        <div className="flex items-start lg:items-center justify-center w-full min-h-full m-auto p-0 lg:p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-overdue-cancel-title"
            className="relative flex flex-col items-center w-full min-h-full lg:min-h-0 max-w-none lg:max-w-[480px] bg-white rounded-none lg:rounded-xl px-3 pt-9 pb-5 lg:px-7 lg:pt-10 lg:pb-6">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 text-gray-5">
              <CloseModalIcon className="size-6" />
            </button>

            <div className="flex flex-col gap-1 lg:gap-3 items-center w-full mb-6">
              <div className="flex flex-col gap-2 items-center lg:contents">
                <div className="font-jost text-xs lg:text-sm font-semibold leading-4 lg:leading-5 text-red-1 uppercase">
                  Payment overdue
                </div>
                <div className="flex flex-col gap-1 lg:gap-2.5 items-center w-full">
                  <div
                    id="payment-overdue-cancel-title"
                    className="font-jost text-[20px] lg:text-[28px] font-semibold leading-[26px] lg:leading-8 text-dark-1 text-center normal-case !mb-0">
                    Cancel your BOTB Pass?
                  </div>
                  <div className="font-jost text-sm lg:text-lg font-normal leading-5 lg:leading-6 text-gray-1 text-center tracking-[0]">
                    Cancelling now ends your Pass immediately and voids {totalTickets} BOTB Pass tickets across{" "}
                    {totalCompetitions} competitions
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 w-full mb-8">
              {/* Savings highlight */}
              <div className="relative overflow-hidden border border-black/8 rounded-xl bg-gradient-to-b from-[#f8f1fc] to-[#fefdfe] h-20 lg:h-[88px]">
                <img
                  src="/assets/images/botb-pass-members/coins.png"
                  alt=""
                  className="absolute left-[11px] lg:left-[19px] bottom-[5px] lg:bottom-1.5 size-[68px] lg:size-[75px] pointer-events-none select-none object-cover"
                />
                <div className="relative flex flex-col items-center justify-center h-full text-center px-4">
                  <div className="bg-gradient-botb bg-clip-text text-transparent font-jost text-[10px] lg:text-xs font-semibold leading-3 lg:leading-4 uppercase">
                    Your Pass has saved you
                  </div>
                  <div className="bg-gradient-botb bg-clip-text text-transparent font-jost text-[18px] lg:text-[22px] font-bold leading-6 lg:leading-7">
                    {savingsLabel}
                  </div>
                  <div className="font-jost text-xs lg:text-[13px] font-medium leading-4 lg:leading-4.5 text-gray-1">
                    Saved vs. paying full ticket price
                  </div>
                </div>
              </div>

              {/* Competitions accordion */}
              <div className="border border-black/8 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-3 lg:px-4 min-h-[72px] lg:min-h-[88px] py-3">
                  <div className="flex flex-col gap-1 items-start min-w-0">
                    <div className="flex flex-col items-start">
                      <div className="font-jost text-xs lg:text-[13px] font-semibold leading-4 lg:leading-4.5 text-dark-1 -mb-px lg:mb-0">
                        You&apos;re currently playing for
                      </div>
                      <div className="font-jost text-base lg:text-lg font-bold leading-[22px] lg:leading-6 text-dark-1">
                        {totalCompetitions} {totalCompetitions === 1 ? "Competition" : "Competitions"}
                      </div>
                    </div>
                    <div className="font-jost text-[11px] lg:text-xs font-medium leading-3.5 lg:leading-4 text-gray-5">
                      Voided if you cancel
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsCompetitionsExpanded(prev => !prev)}
                    aria-expanded={isCompetitionsExpanded}
                    className="inline-flex items-center gap-1 shrink-0 text-primary font-jost text-xs lg:text-sm font-medium leading-4 lg:leading-5">
                    View competitions
                    <ChevronDownIcon
                      className={`size-5 text-primary transition-transform ${isCompetitionsExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>

                {isCompetitionsExpanded && (
                  <div className="px-3 lg:px-4 py-4 border-t border-black/8">
                    {/* <div className="w-full h-px bg-black/8 mb-4" /> */}
                    <div className="flex items-center justify-center gap-4 lg:gap-6 mb-4 border-b border-black/8">
                      <button
                        type="button"
                        onClick={() => setActiveTab("featured")}
                        className={`flex-1 relative pb-[13px] font-jost text-xs lg:text-sm font-medium leading-4 lg:leading-5 whitespace-nowrap ${
                          activeTab === "featured" ? "text-primary" : "text-gray-5"
                        }`}>
                        Featured Competitions ({featured.length})
                        {activeTab === "featured" && (
                          <span
                            aria-hidden="true"
                            className="block absolute inset-x-0 !top-auto -bottom-px h-px bg-primary"
                          />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab("exclusive")}
                        className={`flex-1 relative pb-[13px] font-jost text-xs lg:text-sm font-medium leading-4 lg:leading-5 whitespace-nowrap ${
                          activeTab === "exclusive" ? "text-primary" : "text-gray-5"
                        }`}>
                        BOTB Pass Exclusive ({exclusive.length})
                        {activeTab === "exclusive" && (
                          <span
                            aria-hidden="true"
                            className="block absolute inset-x-0 !top-auto -bottom-px h-px bg-primary"
                          />
                        )}
                      </button>
                    </div>

                    <div className="flex flex-col gap-3">
                      {activeCompetitions.length === 0 ? (
                        <div className="font-jost text-sm font-medium leading-5 text-gray-5 text-center py-2">
                          No competitions in this category
                        </div>
                      ) : (
                        activeCompetitions.map((competition, index) => (
                          <CompetitionRow
                            key={competition.CompetitionId}
                            competition={competition}
                            isLast={index === activeCompetitions.length - 1}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:gap-4 items-center w-full">
              <div className="flex flex-col gap-3 lg:gap-4 w-full">
                <button
                  type="button"
                  onClick={handlePayAndStay}
                  className="theme-btn theme-btn-primary w-full uppercase min-h-11 lg:min-h-[52px] text-sm lg:text-base font-semibold leading-5 lg:leading-5.5">
                  Pay {amountLabel} and stay in
                </button>
                <button
                  type="button"
                  disabled={isCancelling}
                  onClick={handleCancelAndVoid}
                  className="theme-btn theme-btn-primary-outline w-full uppercase text-primary min-h-11 lg:min-h-[52px] text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-5.5">
                  {isCancelling ? "Cancelling..." : "Cancel and void"}
                </button>
              </div>
              <div className="font-jost text-xs lg:text-[13px] font-normal leading-4 lg:leading-4.5 text-gray-5 text-center tracking-[0]">
                Cancellation takes effect immediately.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
